import { OrganizationMembership, MembershipStatus } from '../models/organization-membership';
import { OrganizationRole } from '../models/organization';
import { logEvent } from './analytics-service';
import { log } from './production-logger';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  limit,
  orderBy,
  runTransaction,
  addDoc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { auth, firestore } from '../firebase/firebase';

/**
 * Service for managing organization memberships
 */
class OrganizationMembershipService {
  // Protection against recursive calls that could cause stack overflow
  private activeCalls = new Set<string>();
  /**
   * Create a new organization membership
   *
   * @param userId - User ID
   * @param organizationId - Organization ID
   * @param role - User's role in the organization
   * @param isPrimary - Whether this is the user's primary organization
   * @param invitedBy - User ID of the person who invited this user
   * @returns Promise with created membership
   */
  async createMembership(
    userId: string,
    organizationId: string,
    role: OrganizationRole,
    isPrimary: boolean = false,
    invitedBy?: string,
  ): Promise<OrganizationMembership> {
    try {
      // Direct usage of firestore instance

      // Check if the user already has a membership in this organization
      const existingMembership = await this.getUserMembershipInOrganization(userId, organizationId);
      if (existingMembership) {
        throw new Error('User is already a member of this organization');
      }

      // If setting as primary, ensure no other membership is primary
      if (isPrimary) {
        await this.clearPrimaryMembership(userId);
      }

      // Current timestamp
      const now = new Date().toISOString();

      // Create the membership document
      const membershipRef = doc(collection(firestore, 'memberships'));

      const membership: OrganizationMembership = {
        id: membershipRef.id,
        userId,
        organizationId,
        role,
        isPrimary,
        joinedAt: now,
        invitedBy,
        createdAt: now,
        updatedAt: now,
      };

      // Save the membership document
      await setDoc(membershipRef, membership);

      // Update the organization to add the member ID to the memberIds array
      await runTransaction(firestore, async transaction => {
        const orgRef = doc(firestore, 'organizations', organizationId);
        const orgDoc = await transaction.get(orgRef);

        if (!orgDoc.exists()) {
          throw new Error('Organization not found');
        }

        const memberIds = orgDoc.data().memberIds || [];
        if (!memberIds.includes(userId)) {
          transaction.update(orgRef, {
            memberIds: [...memberIds, userId],
            updatedAt: now,
          });
        }
      });

      logEvent('create_organization_membership', {
        userId,
        organizationId,
        role,
        isPrimary,
      });

      return membership;
    } catch (error) {
      log.error('Error creating organization membership:', error);
      throw new Error('Failed to create organization membership');
    }
  }

  /**
   * Get a membership by ID
   *
   * @param membershipId - Membership ID
   * @returns Promise with the membership
   */
  async getMembershipById(membershipId: string): Promise<OrganizationMembership | null> {
    try {
      // Direct usage of firestore instance

      const membershipDoc = await getDoc(doc(firestore, 'memberships', membershipId));

      if (!membershipDoc.exists()) {
        return null;
      }

      const data = membershipDoc.data();
      
      // Helper function to safely convert date fields
      const convertDate = (dateField: any): string => {
        if (!dateField) return new Date().toISOString();
        if (typeof dateField === 'string') return dateField;
        if (dateField.toDate) return dateField.toDate().toISOString();
        return new Date().toISOString();
      };
      
      return {
        id: membershipDoc.id,
        userId: data.userId,
        organizationId: data.organizationId,
        role: data.role || OrganizationRole.MEMBER,
        isPrimary: data.isPrimary || false,
        joinedAt: data.joinedAt || convertDate(data.createdAt),
        createdAt: convertDate(data.createdAt),
        updatedAt: data.updatedAt ? convertDate(data.updatedAt) : undefined,
        invitedBy: data.invitedBy,
        permissions: data.permissions,
      } as OrganizationMembership;
    } catch (error) {
      log.error('Error getting organization membership:', error);
      throw new Error('Failed to get organization membership');
    }
  }

  /**
   * Get all memberships for a user
   *
   * @param userId - User ID
   * @returns Promise with an array of memberships
   */
  async getUserMemberships(userId: string): Promise<OrganizationMembership[]> {
    const callKey = `getUserMemberships:${userId}`;
    
    // Prevent recursive calls that could cause stack overflow
    if (this.activeCalls.has(callKey)) {
      log.warn('🔄 Preventing recursive getUserMemberships call for:', userId);
      return [];
    }
    
    this.activeCalls.add(callKey);
    
    try {
      // Direct usage of firestore instance

      // Query memberships where userId matches
      const membershipsCollection = collection(firestore, 'memberships');
      const q = query(membershipsCollection, where('userId', '==', userId));
      const snapshot = await getDocs(q);

      // Helper function to safely convert date fields
      const convertDate = (dateField: any): string => {
        if (!dateField) return new Date().toISOString();
        if (typeof dateField === 'string') return dateField;
        if (dateField.toDate) return dateField.toDate().toISOString();
        return new Date().toISOString();
      };
      
      // Map results to OrganizationMembership objects
      const memberships: OrganizationMembership[] = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        memberships.push({
          id: doc.id,
          userId: data.userId,
          organizationId: data.organizationId,
          role: data.role || OrganizationRole.MEMBER,
          isPrimary: data.isPrimary || false,
          joinedAt: data.joinedAt || convertDate(data.createdAt),
          createdAt: convertDate(data.createdAt),
          updatedAt: data.updatedAt ? convertDate(data.updatedAt) : undefined,
          invitedBy: data.invitedBy,
          permissions: data.permissions,
        } as OrganizationMembership);
      });

      return memberships;
    } catch (error) {
      log.error('Error fetching user memberships:', error);
      return [];
    } finally {
      this.activeCalls.delete(callKey);
    }
  }

  /**
   * Get a user's primary organization membership
   *
   * @param userId - User ID
   * @returns Promise with the primary membership or null if none found
   */
  async getUserPrimaryMembership(userId: string): Promise<OrganizationMembership | null> {
    try {
      // Direct usage of firestore instance

      // Query memberships where userId matches and isPrimary is true
      const membershipsCollection = collection(firestore, 'memberships');
      const q = query(
        membershipsCollection,
        where('userId', '==', userId),
        where('isPrimary', '==', true),
      );
      const snapshot = await getDocs(q);

      // Return the first (and should be only) primary membership
      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      const data = doc.data();
      
      // Helper function to safely convert date fields
      const convertDate = (dateField: any): string => {
        if (!dateField) return new Date().toISOString();
        if (typeof dateField === 'string') return dateField;
        if (dateField.toDate) return dateField.toDate().toISOString();
        return new Date().toISOString();
      };

      return {
        id: doc.id,
        userId: data.userId,
        organizationId: data.organizationId,
        role: data.role || OrganizationRole.MEMBER,
        isPrimary: true,
        joinedAt: data.joinedAt || convertDate(data.createdAt),
        createdAt: convertDate(data.createdAt),
        updatedAt: data.updatedAt ? convertDate(data.updatedAt) : undefined,
        invitedBy: data.invitedBy,
        permissions: data.permissions,
      } as OrganizationMembership;
    } catch (error) {
      log.error('Error fetching primary membership:', error);
      return null;
    }
  }

  /**
   * Get a user's membership in a specific organization
   *
   * @param userId - User ID
   * @param organizationId - Organization ID
   * @returns Promise with the membership or null if not found
   */
  async getUserMembershipInOrganization(
    userId: string,
    organizationId: string,
  ): Promise<OrganizationMembership | null> {
    try {
      // Direct usage of firestore instance

      const membershipQuery = query(
        collection(firestore, 'memberships'),
        where('userId', '==', userId),
        where('organizationId', '==', organizationId),
        limit(1),
      );

      const querySnapshot = await getDocs(membershipQuery);

      if (querySnapshot.empty) {
        return null;
      }

      const docData = querySnapshot.docs[0].data();
      
      // Helper function to safely convert date fields
      const convertDate = (dateField: any): string => {
        if (!dateField) return new Date().toISOString();
        if (typeof dateField === 'string') return dateField;
        if (dateField.toDate) return dateField.toDate().toISOString();
        return new Date().toISOString();
      };
      
      return {
        id: querySnapshot.docs[0].id,
        userId: docData.userId,
        organizationId: docData.organizationId,
        role: docData.role || OrganizationRole.MEMBER,
        isPrimary: docData.isPrimary || false,
        joinedAt: docData.joinedAt || convertDate(docData.createdAt),
        createdAt: convertDate(docData.createdAt),
        updatedAt: docData.updatedAt ? convertDate(docData.updatedAt) : undefined,
        invitedBy: docData.invitedBy,
        permissions: docData.permissions,
      } as OrganizationMembership;
    } catch (error) {
      log.error('Error getting user membership in organization:', error);
      throw new Error('Failed to get user membership in organization');
    }
  }

  /**
   * Clear a user's primary membership
   *
   * @param userId - User ID
   */
  async clearPrimaryMembership(userId: string): Promise<void> {
    try {
      // Direct usage of firestore instance

      const primaryMembershipQuery = query(
        collection(firestore, 'memberships'),
        where('userId', '==', userId),
        where('isPrimary', '==', true),
      );

      const querySnapshot = await getDocs(primaryMembershipQuery);

      const updatePromises = querySnapshot.docs.map(doc => {
        return updateDoc(doc.ref, {
          isPrimary: false,
          updatedAt: new Date().toISOString(),
        });
      });

      await Promise.all(updatePromises);
    } catch (error) {
      log.error('Error clearing primary membership:', error);
      throw new Error('Failed to clear primary membership');
    }
  }

  /**
   * Set a membership as primary
   * @param userId - User ID
   * @param organizationId - Organization ID
   * @returns Promise that resolves when the operation is complete
   */
  async setPrimaryMembership(userId: string, organizationId: string): Promise<void> {
    const callKey = `${userId}:${organizationId}`;
    
    // Prevent recursive calls that could cause stack overflow
    if (this.activeCalls.has(callKey)) {
      log.warn('🔄 Preventing recursive setPrimaryMembership call for:', callKey);
      return;
    }
    
    this.activeCalls.add(callKey);
    
    try {
      // Direct usage of firestore instance

      // First find all user's memberships
      const membershipsCollection = collection(firestore, 'memberships');
      const q = query(membershipsCollection, where('userId', '==', userId));
      const querySnapshot = await getDocs(q);

      // Start a batch operation to update multiple documents atomically
      const batch = writeBatch(firestore);

      // Set all memberships to non-primary
      querySnapshot.forEach(doc => {
        batch.update(doc.ref, {
          isPrimary: doc.data().organizationId === organizationId,
          updatedAt: serverTimestamp(),
        });
      });

      // If the target membership doesn't exist yet, create it
      if (!querySnapshot.docs.some(doc => doc.data().organizationId === organizationId)) {
        const membershipId = `${userId}_${organizationId}`;
        const newMembershipRef = doc(firestore, 'memberships', membershipId);
        const now = new Date().toISOString();

        batch.set(newMembershipRef, {
          userId,
          organizationId,
          role: OrganizationRole.MEMBER,
          isPrimary: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          joinedAt: now,
          status: 'active',
        });
      }

      // Commit all changes
      await batch.commit();

      log.debug(`Set primary membership for user ${userId} to organization ${organizationId}`);
    } catch (error) {
      log.error('Error setting primary membership:', error);
      throw new Error('Failed to set primary membership');
    } finally {
      this.activeCalls.delete(callKey);
    }
  }

  /**
   * Update a membership's role
   *
   * @param membershipId - Membership ID
   * @param role - New role
   */
  async updateMembershipRole(membershipId: string, role: OrganizationRole): Promise<void> {
    try {
      // Direct usage of firestore instance

      await updateDoc(doc(firestore, 'memberships', membershipId), {
        role,
        updatedAt: new Date().toISOString(),
      });
    } catch (error) {
      log.error('Error updating membership role:', error);
      throw new Error('Failed to update membership role');
    }
  }

  /**
   * Delete a membership
   *
   * @param membershipId - Membership ID
   */
  async deleteMembership(membershipId: string): Promise<boolean> {
    try {
      // Direct usage of auth and firestore instances
      const user = auth.currentUser;

      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get the membership to ensure it belongs to the current user
      const membershipDoc = await getDoc(doc(firestore, 'memberships', membershipId));

      if (!membershipDoc.exists()) {
        throw new Error('Membership not found');
      }

      const membershipData = membershipDoc.data();

      // Only allow users to delete their own memberships unless they're an admin
      if (membershipData.userId !== user.uid) {
        throw new Error('Cannot delete membership for another user');
      }

      // Check if this is the primary membership
      if (membershipData.isPrimary) {
        // Find another membership to make primary
        const memberships = await this.getUserMemberships(user.uid);
        const otherMemberships = memberships.filter(m => m.id !== membershipId);

        if (otherMemberships.length > 0) {
          // Make another membership primary
          await this.setPrimaryMembership(user.uid, otherMemberships[0].organizationId);
        }
      }

      // Delete the membership
      await deleteDoc(doc(firestore, 'memberships', membershipId));

      return true;
    } catch (error) {
      log.error('Error deleting membership:', error);
      return false;
    }
  }

  /**
   * Create a simple membership with minimal data
   * @param membershipData - Membership data to create
   * @returns Promise with the created membership ID
   */
  async createUserMembership(membershipData: {
    userId: string;
    organizationId: string;
    role: string;
    isPrimary: boolean;
  }): Promise<string> {
    try {
      // Direct usage of firestore instance

      // Generate a custom ID by combining userId and organizationId
      const membershipId = `${membershipData.userId}_${membershipData.organizationId}`;
      const membershipRef = doc(firestore, 'memberships', membershipId);

      const now = new Date().toISOString();

      await setDoc(membershipRef, {
        userId: membershipData.userId,
        organizationId: membershipData.organizationId,
        role: membershipData.role,
        isPrimary: membershipData.isPrimary,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        joinedAt: now,
        status: 'active',
      });

      return membershipId;
    } catch (error) {
      log.error('Error creating membership:', error);
      throw new Error('Failed to create membership');
    }
  }

  /**
   * Validate organization membership data
   * @param membershipData - The membership data to validate
   * @returns Array of validation error messages, empty if valid
   */
  validateOrganizationMembership(membershipData: {
    userId: string;
    organizationId: string;
    role?: string;
    isPrimary?: boolean;
  }): string[] {
    const errors: string[] = [];

    // Required fields
    if (!membershipData.userId) {
      errors.push('User ID is required');
    }
    if (!membershipData.organizationId) {
      errors.push('Organization ID is required');
    }

    // Validate role if provided
    if (membershipData.role) {
      const validRoles = Object.values(OrganizationRole);
      if (!validRoles.includes(membershipData.role as OrganizationRole)) {
        errors.push(`Role must be one of: ${validRoles.join(', ')}`);
      }
    }

    // Validate isPrimary if provided
    if (membershipData.isPrimary !== undefined && typeof membershipData.isPrimary !== 'boolean') {
      errors.push('isPrimary must be a boolean value');
    }

    return errors;
  }

  /**
   * Ensure a user has at least one primary membership
   * @param userId - User ID to check
   */
  async ensureUserHasPrimaryMembership(userId: string): Promise<boolean> {
    try {
      // Get user's memberships
      const memberships = await this.getUserMemberships(userId);

      // If no memberships, nothing to do
      if (memberships.length === 0) {
        return false;
      }

      // Check if user has a primary membership
      const hasPrimary = memberships.some(membership => membership.isPrimary);

      // If no primary, set the first one as primary
      if (!hasPrimary && memberships.length > 0) {
        await this.setPrimaryMembership(userId, memberships[0].organizationId);
        return true;
      }

      return hasPrimary;
    } catch (error) {
      log.error('Error ensuring user has primary membership:', error);
      return false;
    }
  }

  /**
   * Get all memberships for an organization
   * @param organizationId - Organization ID
   * @returns Promise with array of memberships
   */
  async getOrganizationMemberships(organizationId: string): Promise<OrganizationMembership[]> {
    try {
      // Direct usage of firestore instance
      
      const membershipsCollection = collection(firestore, 'memberships');
      const q = query(
        membershipsCollection, 
        where('organizationId', '==', organizationId),
        orderBy('joinedAt', 'desc')
      );
      const snapshot = await getDocs(q);
      
      // Helper function to safely convert date fields
      const convertDate = (dateField: any): string => {
        if (!dateField) return new Date().toISOString();
        if (typeof dateField === 'string') return dateField;
        if (dateField.toDate) return dateField.toDate().toISOString();
        return new Date().toISOString();
      };
      
      const memberships: OrganizationMembership[] = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        memberships.push({
          id: doc.id,
          userId: data.userId,
          organizationId: data.organizationId,
          role: data.role || OrganizationRole.MEMBER,
          isPrimary: data.isPrimary || false,
          isActive: data.isActive !== false, // Default to true if not specified
          joinedAt: data.joinedAt || convertDate(data.createdAt),
          createdAt: convertDate(data.createdAt),
          updatedAt: data.updatedAt ? convertDate(data.updatedAt) : undefined,
          invitedBy: data.invitedBy,
          permissions: data.permissions,
        } as OrganizationMembership);
      });
      
      return memberships;
    } catch (error) {
      log.error('Error getting organization memberships:', error);
      throw new Error('Failed to get organization memberships');
    }
  }

  /**
   * Update membership status (active/inactive)
   * @param membershipId - Membership ID
   * @param isActive - New status
   */
  async updateMembershipStatus(membershipId: string, isActive: boolean): Promise<void> {
    try {
      // Direct usage of firestore instance
      await updateDoc(doc(firestore, 'memberships', membershipId), {
        isActive,
        updatedAt: serverTimestamp(),
      });
      
      logEvent('update_membership_status', { membershipId, isActive });
    } catch (error) {
      log.error('Error updating membership status:', error);
      throw new Error('Failed to update membership status');
    }
  }

  /**
   * Update member role
   * @param membershipId - Membership ID
   * @param newRole - New role
   */
  async updateMemberRole(membershipId: string, newRole: OrganizationRole): Promise<void> {
    try {
      // Direct usage of firestore instance
      await updateDoc(doc(firestore, 'memberships', membershipId), {
        role: newRole,
        updatedAt: serverTimestamp(),
      });
      
      logEvent('update_member_role', { membershipId, newRole });
    } catch (error) {
      log.error('Error updating member role:', error);
      throw new Error('Failed to update member role');
    }
  }

  /**
   * Remove a membership
   * @param organizationId - Organization ID
   * @param userId - User ID to remove
   */
  async removeMembership(organizationId: string, userId: string): Promise<void> {
    try {
      // Direct usage of firestore instance
      
      // Find the membership
      const membership = await this.getUserMembershipInOrganization(userId, organizationId);
      if (!membership) {
        throw new Error('Membership not found');
      }
      
      // Don't allow removing organization owner
      if (membership.role === OrganizationRole.OWNER) {
        throw new Error('Cannot remove organization owner');
      }
      
      // Delete the membership
      await deleteDoc(doc(firestore, 'memberships', membership.id));
      
      // Update organization memberIds
      await runTransaction(firestore, async transaction => {
        const orgRef = doc(firestore, 'organizations', organizationId);
        const orgDoc = await transaction.get(orgRef);
        
        if (orgDoc.exists()) {
          const memberIds = orgDoc.data().memberIds || [];
          const updatedMemberIds = memberIds.filter((id: string) => id !== userId);
          
          transaction.update(orgRef, {
            memberIds: updatedMemberIds,
            updatedAt: serverTimestamp(),
          });
        }
      });
      
      logEvent('remove_membership', { organizationId, userId });
    } catch (error) {
      log.error('Error removing membership:', error);
      throw error;
    }
  }
}

export default new OrganizationMembershipService();
